import React, { CSSProperties, useContext } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextBody2Bold, MBTextSubtitle } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import classNames from './DropTarget.module.css';

interface Props {
  placeholder: string;
  state: 'inactive' | 'active' | 'error';
  acceptedContentTypes?: string[];
  style?: CSSProperties;
  mode: 'lean' | 'full';
  loading?: boolean;
}

export function DropTarget({
  placeholder,
  state,
  acceptedContentTypes,
  style,
  mode,
  loading,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const iconName = getIcon(acceptedContentTypes, mode);
  const responsiveForms = useResponsiveFormContext();
  const isInactive = state === 'inactive';
  const isActive = state === 'active';
  const isError = state === 'error';

  return (
    <View
      className={[
        classNames.dropTarget,
        isInactive && classNames.inactive,
        isActive && classNames.active,
        isError && classNames.error,
        responsiveForms && classNames.responsiveForms,
      ]}
      style={style}
    >
      <View row fill center paddingLeft={1} paddingRight={3}>
        {loading && <MBLoadingIndicator style={{ minHeight: 40 }} />}
        {!loading && (
          <>
            <View key={iconName} marginRight={1}>
              <MBIconV2 name={iconName} color={colors.foregroundActive} />
            </View>
            <MBTextSubtitle
              color={loading ? colors.foregroundInactive : colors.foregroundActive}
              style={{ flexGrow: 0, flexShrink: 1 }}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {placeholder}
            </MBTextSubtitle>
          </>
        )}
      </View>
      {!loading && state === 'error' && (
        <MBTextBody2Bold color={colors.error.foreground} align={'center'}>
          {i18n('errors.file')}
        </MBTextBody2Bold>
      )}
    </View>
  );
}

function getIcon(acceptedContentTypes: string[] | undefined, mode: 'lean' | 'full') {
  if (mode === 'lean') {
    return 'camera';
  }
  if (!acceptedContentTypes) {
    return 'file';
  }
  if (acceptedContentTypes.some(c => c.startsWith('image'))) {
    return 'image-portrait';
  }
  if (acceptedContentTypes.some(c => c.startsWith('video'))) {
    return 'video-recorder';
  }
  return 'file';
}
